import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNotifications } from '../../../contexts/notification';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBell, 
  FaClock, 
  FaTimes,
  FaRegFileAlt,
  FaCheckCircle,
  FaRegEnvelope,
  FaBriefcase,
  FaRegCalendarCheck,
  FaInfoCircle,
  FaRegLightbulb
} from 'react-icons/fa';
import { 
  // Glass effects
  glassDeep, 
  glassMedium,
  glassError,
  
  // Animations
  fadeInAnimation,
  scaleInAnimation,
  pulseAnimation,
} from '../../../styles/components';

// Animation keyframes
const bellRingAnimation = keyframes`
  0%   { transform: rotate(0); }
  15%  { transform: rotate(15deg); }
  30%  { transform: rotate(-15deg); }
  45%  { transform: rotate(8deg); }
  60%  { transform: rotate(-8deg); }
  75%  { transform: rotate(4deg); }
  85%  { transform: rotate(-4deg); }
  92%  { transform: rotate(2deg); }
  100% { transform: rotate(0); }
`;

const badgePulse = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(-10px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const BellContainer = styled.div`
  position: relative;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  background: transparent;
  border-radius: 12px;
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(99, 102, 241, 0.08);
  }
  
  &:active {
    transform: translateY(0px);
  }
`;

const Bell = styled.div`
  color: #f1f5f9;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: white;
  }
  
  svg {
    width: 22px;
    height: 22px;
    stroke-width: 2px;
    transition: transform 0.2s ease;
  }
  
  ${props => props.hasUnread && css`
    animation: ${bellRingAnimation} 1.2s ease-in-out;
    color: rgba(99, 102, 241, 0.9);
    
    &:hover {
      color: rgba(99, 102, 241, 1);
    }
  `}
`;

const Badge = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  background: ${props => props.isOpen ? 'white' : '#ef4444'};
  color: ${props => props.isOpen ? '#ef4444' : 'white'};
  border-radius: 10px;
  min-width: ${props => props.count > 9 ? '18px' : '16px'};
  height: 16px;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  box-shadow: ${props => props.isOpen 
    ? '0 2px 5px rgba(255, 255, 255, 0.4)' 
    : '0 2px 5px rgba(239, 68, 68, 0.4)'};
  animation: ${badgePulse} 1.5s infinite ease-in-out;
  padding: ${props => props.count > 9 ? '0 4px' : '0'};
  transition: all 0.2s ease;
`;

const NotificationPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: -8px;
  width: 400px; /* Slightly narrower for better readability */
  max-width: 95vw; /* Uses more screen space on small devices */
  max-height: 550px; /* Shows more content */
  animation: ${slideIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.25), 
              0 8px 10px -6px rgba(0, 0, 0, 0.1),
              0 0 0 1px rgba(99, 102, 241, 0.2);
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  overflow: hidden;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:before {
    content: '';
    position: absolute;
    top: -8px;
    right: 16px;
    transform: rotate(45deg);
    width: 16px;
    height: 16px;
    border-radius: 3px;
    background: rgba(15, 23, 42, 0.95);
    z-index: -1;
    border-top: 1px solid rgba(99, 102, 241, 0.3);
    border-left: 1px solid rgba(99, 102, 241, 0.3);
  }
`;

const NotificationHeader = styled.div`
  padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.lg}`};
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(15, 23, 42, 1);
  
  h3 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.md};
    color: white;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    letter-spacing: 0.3px;
  }
  
  button {
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: rgba(99, 102, 241, 0.9);
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 8px;
    transition: all 0.2s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.15);
      border-color: rgba(99, 102, 241, 0.5);
      transform: translateY(-1px);
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

const NotificationList = styled.div`
  max-height: 450px;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 4px;
    background: rgba(15, 23, 42, 0.3);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 4px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.7);
    }
  }
`;

const fadeOut = keyframes`
  from { 
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(10px);
  }
`;

const NotificationItem = styled.div`
  /* Layout & Spacing */
  padding: 16px 20px;
  position: relative;
  
  /* Visual styling */
  border-left: ${props => props.read ? '0px' : '3px'} solid rgba(99, 102, 241, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: ${props => props.read 
    ? 'rgba(30, 41, 59, 0.6)' 
    : 'rgba(30, 41, 59, 0.8)'
  };
  
  /* Text handling */
  word-break: break-word;
  overflow-wrap: break-word;
  
  /* Interaction */
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(30, 41, 59, 0.9);
    transform: translateX(2px);
    box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.3);
  }
  
  &:last-child {
    border-bottom: none;
  }

  &.deleting {
    animation: ${fadeOut} 0.3s forwards;
    pointer-events: none;
  }
`;

const NotificationTitle = styled.div`
  /* Typography */
  font-weight: 600;
  color: white;
  font-size: 15px;
  
  /* Spacing */
  margin: 0 0 8px 0;
  padding-right: 25px;
  line-height: 1.4;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    flex-shrink: 0;
  }
`;

const NotificationMessage = styled.div`
  /* Typography */
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  
  /* Layout */
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding-right: 25px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const NotificationTime = styled.div`
  /* Typography */
  font-size: 12px;
  color: rgba(99, 102, 241, 0.8);
  
  /* Spacing */
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    font-size: 10px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing.xl2} ${theme.spacing.xl}`};
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-height: 200px;
  
  svg {
    margin-bottom: 16px;
    opacity: 0.6;
    color: rgba(99, 102, 241, 0.5);
  }
  
  div {
    font-weight: 500;
    letter-spacing: 0.3px;
  }
`;

const DeleteButton = styled.button`
  /* Positioning */
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 10;
  
  /* Dimensions */
  width: 22px;
  height: 22px;
  padding: 0;
  
  /* Appearance */
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  
  /* Animation */
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 59, 48, 0.15);
    color: rgba(255, 59, 48, 0.9);
    transform: scale(1.1);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 59, 48, 0.4);
  }
  
  ${NotificationItem}:hover & {
    opacity: 1;
  }
`;

const UnreadIndicator = styled.div`
  width: 6px;
  height: 6px;
  background-color: #3b82f6;
  border-radius: 50%;
  position: absolute;
  top: 14px;
  right: 42px;
  box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
  animation: ${pulseAnimation} 2s infinite ease-in-out;
`;

/**
 * NotificationBell component displays a bell icon with a badge showing unread notifications count
 * and a dropdown panel with notification items.
 * 
 * Implements the futuristic, modern, semi-glassy design language of the SCAD system.
 */
const NotificationBell = () => {
  // Component state
  const [isOpen, setIsOpen] = useState(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  
  // References
  const containerRef = useRef(null);
  
  // Context hooks
  const { 
    notifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();

  // ==============================
  // Side Effects
  // ==============================

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add and clean up event listeners
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animate bell when new notifications arrive
  useEffect(() => {
    if (getUnreadCount() > 0) {
      // Start animation
      setBellAnimating(true);
      
      // Stop animation after delay
      const timer = setTimeout(() => setBellAnimating(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [getUnreadCount]);

  // ==============================
  // Event Handlers
  // ==============================
  
  /**
   * Handle notification click - mark as read and navigate if needed
   */
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Handle navigation based on notification type
    if (notification.data?.workshopId) {
      console.log('Navigate to workshop:', notification.data.workshopId);
    } else if (notification.type === 'application') {
      console.log('Navigate to application:', notification.data?.applicationId);
    } else if (notification.type === 'status') {
      console.log('Navigate to status update:', notification.data?.statusId);
    }
  };

  /**
   * Handle delete button click - stop propagation and delete notification
   * Adds a fade out animation before removing the notification
   */
  const handleDeleteClick = (e, notificationId) => {
    e.stopPropagation();
    
    // Get the notification element and add deleting class for animation
    const notificationElement = e.currentTarget.closest('.notification-item');
    if (notificationElement) {
      notificationElement.classList.add('deleting');
      
      // Wait for animation to complete before removing from state
      setTimeout(() => {
        deleteNotification(notificationId);
      }, 300); // Same duration as the animation
    } else {
      // Fallback if element not found
      deleteNotification(notificationId);
    }
  };

  // ==============================
  // Utility Functions
  // ==============================
  
  /**
   * Format timestamp to relative time (e.g., "2h ago")
   */
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notifTime) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    }
    
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d ago`;
  };

  /**
   * Get icon color based on notification type
   */
  const getTypeColor = (type) => {
    switch(type) {
      case 'application': 
        return 'rgba(255, 99, 87, 1)';  // Brighter red for error/important
      case 'status': 
        return 'rgba(130, 177, 255, 1)'; // Brighter blue for status
      case 'workshop': 
        return 'rgba(129, 255, 132, 1)'; // Brighter green for success
      default: 
        return 'white';                  // White for default
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'application':
        return <FaBriefcase size={14} />;
      case 'status':
        return <FaCheckCircle size={14} />;
      case 'cycle':
        return <FaRegCalendarCheck size={14} />;
      case 'report':
        return <FaRegFileAlt size={14} />;
      case 'workshop':
        return <FaRegLightbulb size={14} />;
      case 'appointment':
        return <FaRegCalendarCheck size={14} />;
      case 'message':
        return <FaRegEnvelope size={14} />;
      default:
        return <FaInfoCircle size={14} />;
    }
  };

  // Animation variants for framer-motion
  const panelVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };
  
  const listItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: i => ({ 
      opacity: 1, 
      x: 0, 
      transition: { delay: i * 0.05, duration: 0.2 } 
    }),
    exit: { opacity: 0, x: 10, transition: { duration: 0.1 } }
  };

  return (
    <BellContainer ref={containerRef} onClick={() => setIsOpen(!isOpen)}>
      <Bell hasUnread={bellAnimating || (getUnreadCount() > 0 && !isOpen)}>
        <FaBell />
      </Bell>
      {getUnreadCount() > 0 && (
        <Badge count={getUnreadCount()} isOpen={isOpen}>
          {getUnreadCount() > 99 ? '99+' : getUnreadCount()}
        </Badge>
      )}
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={panelVariants}
            style={{
              position: 'absolute',
              top: '100%',
              right: '-8px',
              zIndex: 100
            }}
            onClick={e => e.stopPropagation()}
          >
            <NotificationPanel>
              <NotificationHeader>
                <h3>Notifications</h3>
                {getUnreadCount() > 0 && (
                  <button onClick={markAllAsRead}>
                    Mark all as read
                  </button>
                )}
              </NotificationHeader>
              <NotificationList>
                <AnimatePresence>
                  {notifications.length > 0 ? (
                    notifications.map((notification, index) => (
                      <motion.div
                        key={notification.id}
                        custom={index}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={listItemVariants}
                      >
                        <NotificationItem
                          read={notification.read}
                          onClick={() => handleNotificationClick(notification)}
                          className="notification-item"
                        >
                          <NotificationTitle>
                            {getNotificationIcon(notification.type)}
                            {notification.title}
                          </NotificationTitle>
                          <NotificationMessage>
                            {notification.message}
                          </NotificationMessage>
                          <NotificationTime>
                            <FaClock size={10} />
                            {formatTimeAgo(notification.timestamp)}
                          </NotificationTime>
                          <DeleteButton
                            onClick={(e) => handleDeleteClick(e, notification.id)}
                            aria-label="Delete notification"
                          >
                            <FaTimes size={12} />
                          </DeleteButton>
                          {!notification.read && <UnreadIndicator />}
                        </NotificationItem>
                      </motion.div>
                    ))
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <EmptyState>
                        <FaRegEnvelope size={32} />
                        <div>No notifications yet</div>
                      </EmptyState>
                    </motion.div>
                  )}
                </AnimatePresence>
              </NotificationList>
            </NotificationPanel>
          </motion.div>
        )}
      </AnimatePresence>
    </BellContainer>
  );
};

export default NotificationBell; 